import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Search } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { Footer } from "../components/Footer";
import { CompactHeader } from "../components/CompactHeader";
import * as Constants from "../../constants";
import BackLoader from "../../components/backloader";
export default function Gallery() {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    onLoadGallery();
  }, [selectedYear, searchTerm]);

  const events = [
    {
      id: 3,
      title: "Prison Decongestion",
      date: "2025-01-09",
      year: "2026",
      img: `${Constants.baseurl}/assets/landing_images/JIS-Gallery/Prison-Decongestion/PRI1.jpg`,
    },
    {
      id: 1,
      title:
        "Magistrates, Registrars and Sheriffs Training on Small Claims Court",
      date: "2025-01-09",
      year: "2025",
      img: `${Constants.baseurl}/assets/landing_images/JIS-Gallery/Magistrates-Registrars-and-Sheriffs-Training-on-Small-Claims-Court/MRSTSCC-1.jpg`,
    },
    {
      id: 2,
      title:
        "Law Day CelebrationSpecial Court Session in Honour of Late Mr. Adesina Ogunlana and Late Olabisi Adegunle",
      date: "2025-02-05",
      year: "2024",
      img: `${Constants.baseurl}/assets/landing_images/JIS-Gallery/SpecialCourtSessioninHonourofLate-Mr-Adesina-Ogunlana-and-Late-Olabisi Adegunle/SCSHLAOLOA-1.jpg`,
    },
  ];

  const years = ["All", ...Array.from(new Set(events.map((e) => e.year)))];
  const onLoadGallery = async () => {
    try {
      setLoading(true);
      const resp = await fetch(
        `${Constants.apiUrl}/cms/gallery/${
          selectedYear == "All" ? 0 : selectedYear
        }/${searchTerm == "" ? "All" : searchTerm}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = await resp.json();
      setLoading(false);
      if (data.code == 0) {
        setGalleries(data.items);
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <CompactHeader />
      {/* Hero */}
      <section className="relative py-8 bg-hero-light-image dark:bg-hero-dark">
        <div className="absolute inset-0 bg-black/5 dark:bg-black/30"></div>
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white">
              Events
            </h1>
          </div>
        </div>
      </section>
      {/* Filters */}
      <section className="py-12 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto max-w-6xl px-4">
          <Card className="shadow-lg dark:bg-gray-900">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-400 text-2xl font-bold">
                <Calendar className="w-6 h-6" /> Browse by Year
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full md:w-1/4 border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                >
                  {years.map((year) => (
                    <option key={year}>
                      {year === "All" ? "All Years" : year}
                    </option>
                  ))}
                </select>

                <div className="relative w-full md:w-1/3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search event title..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Event Grid */}
      <section className="py-12 b-5 bg-white dark:bg-gray-900">
        <div className="container mx-auto max-w-6xl px-4 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {galleries.map((event) => (
              <motion.div
                key={event.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                onClick={() => navigate(`/gallery/${event.id}`)}
                className="cursor-pointer bg-gray-50 dark:bg-gray-800 rounded-xl overflow-hidden shadow hover:shadow-2xl transition-all"
              >
                <img
                  src={`${Constants.docUrl}/cms/gallery/${event.coverImage}`}
                  alt={event.title}
                  className="w-full h-60 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 hover:text-blue-600">
                    {event.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(event.eventOn).toLocaleDateString("en-GB", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </section>
      <Footer />
      <BackLoader open={loading} />
    </div>
  );
}
